import { useEffect, useState, useCallback } from "react";
import { LinkedinLogo, TwitterLogo, FacebookLogo, PlugsConnected, Plugs, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { WebhooksPanel } from "@/components/WebhooksPanel";

const ICONS = { linkedin: LinkedinLogo, twitter: TwitterLogo, facebook: FacebookLogo };
const COLORS = { linkedin: "#0A66C2", twitter: "#111111", facebook: "#1877F2" };

export default function Connections() {
  const { activeId } = useWorkspace();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data } = await api.get(`/connect/status?workspace_id=${activeId}`);
    setStatus(data);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected")) { toast.success(`${p.get("connected")} connected`); window.history.replaceState(null, "", "/connections"); }
    else if (p.get("error")) { toast.error("Connection failed or cancelled"); window.history.replaceState(null, "", "/connections"); }
  }, []);

  const connect = async (platform) => {
    setBusy(platform);
    try {
      const { data } = await api.get(`/connect/${platform}/authorize?workspace_id=${activeId}`);
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot connect yet");
      setBusy(null);
    }
  };

  const disconnect = async (platform) => {
    setBusy(platform);
    try {
      await api.delete(`/connect/${platform}?workspace_id=${activeId}`);
      await load();
      toast.success("Disconnected");
    } catch { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl">
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Connections</h1>
        <p className="text-muted-foreground mt-2">
          Connect your social accounts to auto-publish approved posts. Until connected, publishing is simulated.
        </p>

        <div className="mt-8 space-y-4">
          {status && Object.entries(status).map(([platform, info]) => {
            const Icon = ICONS[platform];
            return (
              <div key={platform} data-testid={`connection-${platform}`}
                className="rounded-lg border border-border bg-card p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: `${COLORS[platform]}15` }}>
                  <Icon size={22} weight="fill" style={{ color: COLORS[platform] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{info.label}</p>
                    {info.connected
                      ? <Badge variant="secondary" className="gap-1"><CheckCircle size={12} weight="fill" className="text-chart-2" /> Connected</Badge>
                      : info.configured
                        ? <Badge variant="outline">Not connected</Badge>
                        : <Badge variant="outline" className="gap-1 text-muted-foreground"><WarningCircle size={12} /> Keys needed</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {info.connected ? (info.account_name || "Account linked")
                      : info.configured ? "Ready to connect"
                      : "Add developer app credentials to enable"}
                  </p>
                </div>
                {info.connected ? (
                  <Button variant="outline" data-testid={`disconnect-${platform}`} onClick={() => disconnect(platform)} disabled={busy === platform}>
                    <Plugs size={16} className="mr-1.5" /> Disconnect
                  </Button>
                ) : (
                  <Button data-testid={`connect-${platform}`} onClick={() => connect(platform)} disabled={busy === platform || !info.configured}>
                    <PlugsConnected size={16} className="mr-1.5" /> Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 rounded-lg border border-dashed border-border p-5">
          <p className="text-sm font-medium mb-1">Need API credentials?</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            LinkedIn requires a Developer App with the <span className="font-mono text-xs">w_member_social</span> permission.
            X/Twitter requires a paid API tier. Once you have the app credentials, share them with me and I'll enable live publishing — the flow above is already wired.
          </p>
        </div>

        <WebhooksPanel activeId={activeId} />
      </div>
    </AppLayout>
  );
}
